import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Check,
  ChevronDown,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  Settings,
  Sun,
  SunMoon,
  User,
} from 'lucide-react'
import { useAppSelector } from '@/hooks/useAppSelector'
import { useAppDispatch } from '@/hooks/useAppDispatch'
import { selectUser, selectTenant, selectTenants, selectCanManage, switchTenant, logoutUser } from '@/features/auth/authSlice'
import { selectIsImpersonating } from '@/features/auth/impersonation'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTheme } from '@/theme/useTheme'
import { isObservabilityEnabled, openFeedbackForm } from '@/observability'
import { assistAnchor } from '@/features/assist/anchors'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { openWorkspace, workspaceHostTarget } from '@/utils/tenantSwitch'

/** The appearance choices, in the order they are offered. */
const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

/**
 * User menu dropdown for header
 * Shows user info and provides logout functionality
 * On mobile (below md breakpoint), also includes the tenant switcher
 * @returns {JSX.Element}
 */
export default function UserMenu() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUser)

  // Tenant data for mobile inline switcher
  const currentTenant = useAppSelector(selectTenant)
  const tenants = useAppSelector(selectTenants)
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const canManage = useAppSelector(selectCanManage)
  const { theme, setTheme } = useTheme()
  // No switching while impersonating: the backend pins the tenant.
  const isImpersonating = useAppSelector(selectIsImpersonating)
  const showTenantSection = !isDesktop && tenants.length > 1 && !isImpersonating

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

  /**
   * Log out and return to the login page, once the confirmation dialog
   * accepts.
   */
  const handleLogoutConfirm = async () => {
    await dispatch(logoutUser())
    navigate('/login', { replace: true })
  }

  /**
   * Handle tenant switch from inline mobile menu
   */
  const handleTenantSelect = async (tenant) => {
    if (tenant.id === currentTenant?.id) return
    try {
      // A workspace host sends it to the other workspace's own host.
      await openWorkspace(tenant, (target) => dispatch(switchTenant(target)).unwrap())
    } catch (error) {
      console.error('Failed to switch tenant:', error)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Topbar control row (F-7 addendum): 36px, matching the other
              icon-scale header controls; the responsive min-h/min-w-11 touch
              bump is already in buttonVariants' base classes. */}
          <Button variant="ghost" size="sm" className="h-9 min-w-9 gap-2 px-1.5 xl:px-2" aria-label={`Account menu for ${user?.name || 'User'}`} {...assistAnchor('nav.account_menu')}>
            {user?.profile_photo_url ? (
              <img
                src={user.profile_photo_url}
                alt={user.name || 'User'}
                width={32}
                height={32}
                className="size-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div aria-hidden="true" className="size-8 shrink-0 rounded-full bg-brand text-brand-fg flex items-center justify-center text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <span data-user-name className="hidden max-w-36 truncate text-body font-medium xl:inline">{user?.name || 'User'}</span>
            <ChevronDown className="hidden size-4 text-ink-2 xl:block" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          {/* User info header */}
          <div className="px-3 py-2">
            <p className="break-words text-body font-medium text-ink">{user?.name || 'User'}</p>
            <p className="text-xs text-ink-2 truncate">{user?.email || ''}</p>
          </div>

          <DropdownMenuSeparator />

          {/* Tenant switcher section - mobile only */}
          {showTenantSection && (
            <>
              <DropdownMenuLabel className="flex items-center gap-2 text-xs text-ink-2 font-normal">
                <Building2 className="w-3.5 h-3.5" />
                Organization
              </DropdownMenuLabel>
              {tenants.map((tenant) => {
                const isCurrent = tenant.id === currentTenant?.id
                const hostTarget = isCurrent ? null : workspaceHostTarget(tenant)
                return (
                  <DropdownMenuItem
                    key={tenant.id}
                    onClick={() => handleTenantSelect(tenant)}
                    className="flex items-center justify-between"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{tenant.name}</span>
                      {hostTarget && (
                        <span className="truncate text-xs text-ink-2">opens {hostTarget.address}</span>
                      )}
                    </span>
                    {isCurrent && (
                      <Check className="w-4 h-4 text-brand shrink-0" />
                    )}
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Navigation items */}
          <DropdownMenuItem onClick={() => navigate('/profile')} {...assistAnchor('account.profile')}>
            <User className="w-4 h-4" />
            Profile
          </DropdownMenuItem>
          {canManage && (
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4" />
              Settings
            </DropdownMenuItem>
          )}

          {/* Appearance — on every breakpoint */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger {...assistAnchor('account.appearance')}>
              <SunMoon className="w-4 h-4" />
              Appearance
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <DropdownMenuRadioItem key={value} value={value}>
                    <Icon className="w-4 h-4" />
                    {label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Feedback - only while the internal-testing harness is on */}
          {isObservabilityEnabled() && (
            <DropdownMenuItem onClick={() => openFeedbackForm()} {...assistAnchor('help.feedback')}>
              <MessageSquare className="w-4 h-4" />
              Provide feedback
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Logout */}
          <DropdownMenuItem
            onClick={() => setLogoutConfirmOpen(true)}
            {...assistAnchor('account.logout')}
            className="text-danger-ink focus:text-danger-ink"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        title="Are you sure you want to log out?"
        confirmLabel="Log out"
        destructive
        onConfirm={handleLogoutConfirm}
      />
    </>
  )
}
